import { IFile } from '@aca-new/app/pages/book/pages/confirmation/shared/components/confirmation-order-summary/shared/interfaces/file.interface';
import { NpsSurveyComponent } from '@aca-new/app/pages/book/pages/confirmation/shared/components/nps-survey/nps-survey.component';
import { ENpsSurveySource } from '@aca-new/app/pages/book/pages/confirmation/shared/components/nps-survey/shaerd/enums/nps-survey-source.enum';
import { NpsSurveyService } from '@aca-new/app/pages/book/pages/confirmation/shared/components/nps-survey/shaerd/services/nps-survey.service';
import { DEFAULT_BUSINESS_SOLUTIONS } from '@aca-new/app/pages/home/shared/constants/default-business-solutions.constants';
import { IDynamicBookingMenus } from '@aca-new/app/pages/main/shared/components/aside/shared/models/interfaces/dynamic-booking-menus.interface';
import { AsideHttpService } from '@aca-new/app/pages/main/shared/components/aside/shared/services/aside.http.service';
import { ILabTestingInvoices, ILabTestingOrderFee } from '@aca-new/app/pages/payments/pages/payments-content/shared/models/interfaces/lab-testing-invoice.interface';
import { IOrderFee } from '@aca-new/app/pages/payments/pages/payments-content/shared/models/interfaces/order-fee.interface';
import { OrderFeeService } from '@aca-new/app/pages/payments/pages/payments-content/shared/services/order-fee/order-fee.service';
import { getDynamicBusinessSolutionLabel } from '@aca-new/app/pages/payments/shared/functions/get-dynamic-business-solution-label.function';
import { EPaypalPopupResult } from '@aca-new/app/pages/payments/shared/models/enums/paypal-popup-result.enum';
import { IEvidenceUploadStatus } from '@aca-new/app/pages/payments/shared/models/interfaces/evidence-upload-status.interface';
import { IPaymentCategories } from '@aca-new/app/pages/payments/shared/models/interfaces/payment-response.interface';
import { PageItemType } from '@aca-new/app/shared/components/data-table/shared/models/interfaces/data-table-data.interface';
import { DialogService } from '@aca-new/app/shared/components/modal/shared/services/dialog.service';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { IFileResponse } from '@aca-new/app/shared/interfaces/file-response.interface';
import { IMyWindow } from '@aca-new/app/shared/interfaces/my-window.interface';
import { IPostMessageData } from '@aca-new/app/shared/interfaces/post-message.interface';
import { EBusinessSolution } from '@aca-new/app/shared/models/enums/business-solution.enum';
import { EDocType } from '@aca-new/app/shared/models/enums/doc-type.enum';
import { ELocalForageKeys } from '@aca-new/app/shared/models/enums/localforage-keys.enum';
import { EPostMessageType } from '@aca-new/app/shared/models/enums/post-message-type.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { HttpResponseBodyNullableType, IHttpResponseBody } from '@aca-new/app/shared/models/interfaces/http-response-body.interface';
import { IInvoiceResponse } from '@aca-new/app/shared/models/interfaces/invoice-response.interface';
import { ISelect } from '@aca-new/app/shared/models/interfaces/select.interface';
import { QimaNullableType } from '@aca-new/app/shared/models/types/qima.type';
import { AppLocationService } from '@aca-new/app/shared/services/browser-services/app-location/app-location-service';
import { AppStringService } from '@aca-new/app/shared/services/exported-services/app-string/app-string.service';
import { AppFileDownloadService } from '@aca-new/app/shared/services/file-services/app-file-download.service';
import { AppFileService } from '@aca-new/app/shared/services/file-services/app-file.service';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { AppOverlayService } from '@aca-new/app/shared/services/modal-services/app-overlay/app-overlay.service';
import { AppPostMessageService } from '@aca-new/app/shared/services/post-message-services/app-post-message/app-post-message.service';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { UserIsolatedLocalForageService } from '@aca-new/app/shared/services/storage-services/user-isolated-localforage.service';
import { WINDOW } from '@aca-new/app/shared/tokens/window.token';
import { HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IQimaFile, QimaOptionalType } from '@qima/ngx-qima';
import { forkJoin, interval, merge, Observable, of, tap } from 'rxjs';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';

const PAYPAL_POPUP_CLOSED_CHECK_INTERVAL_MS = 1000;

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class PaymentsService {
  public constructor(
    @Inject(WINDOW) private readonly _window: IMyWindow,
    private readonly _appLocationService: AppLocationService,
    private readonly _appStringService: AppStringService,
    private readonly _httpService: HttpService,
    private readonly _storageService: StorageService,
    private readonly _userIsolatedLocalForageService: UserIsolatedLocalForageService,
    private readonly _overlayService: AppOverlayService,
    private readonly _appFileDownloadService: AppFileDownloadService,
    private readonly _npsSurveyService: NpsSurveyService,
    private readonly _dialog: DialogService,
    private readonly _appFileService: AppFileService,
    private readonly _orderFeeService: OrderFeeService,
    private readonly _asideHttpService: AsideHttpService,
    private readonly _router: Router,
    private readonly _appPostMessageService: AppPostMessageService
  ) {}

  /**
   * @description
   * Opens the ACA PayPal pop-up and waits for its result.
   * ACA posts the PayPal return status before closing the pop-up; a PayPal cancel,
   * or closing the pop-up without a result, counts as cancelled.
   * @param {string} orderId - Order ids joined by '&' or ','
   * @returns {Observable<EPaypalPopupResult>} Emits the pop-up outcome once
   */
  public payByPaypal$(orderId: string): Observable<EPaypalPopupResult> {
    const acaUrl = this._appLocationService.getAcaUrl();
    const url = `${acaUrl}/paypal-payment/${this._appStringService.commaToAndFunction(orderId)}?referringDomain=myqima`;
    const popup = this._openPopUpWindow(url, null, null);

    if (!popup) {
      return of(EPaypalPopupResult.BLOCKED);
    }

    const acaOrigin = new URL(acaUrl).origin;
    const paypalResult$ = new Observable<EPaypalPopupResult>((subscriber): (() => void) => {
      this._appPostMessageService.addHandler(EPostMessageType.PAYPAL_PAYMENT_RESULT, (event: MessageEvent): void => {
        const message = event.data as IPostMessageData;

        if (event.source !== popup || event.origin !== acaOrigin || message?.type !== EPostMessageType.PAYPAL_PAYMENT_RESULT) {
          return;
        }

        subscriber.next(message.data?.status === 'success' ? EPaypalPopupResult.SUCCESS : EPaypalPopupResult.CANCELLED);
      });

      return (): void => this._appPostMessageService.removeHandler(EPostMessageType.PAYPAL_PAYMENT_RESULT);
    });
    const popupClosed$ = interval(PAYPAL_POPUP_CLOSED_CHECK_INTERVAL_MS).pipe(
      filter((): boolean => popup.closed),
      map((): EPaypalPopupResult => EPaypalPopupResult.CANCELLED)
    );

    return merge(paypalResult$, popupClosed$).pipe(take(1));
  }

  public logVisitHistory$(): Observable<void> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient.post<void>(`${SERVER_URL}/parameter/v2/history?userId=${userId}`, {
      sourceId: 'PAYMENT_EMAIL_TRACKER',
      historyType: 'PAYMENT_EMAIL_TRACKER',
      historyWhen: new Date().toISOString(),
      historyWho: JSON.parse(this._storageService.getItem(EStorageKeys.USER_DATA)).company.name,
      historyAction: 'Jump to unpaid page via reminder email',
    });
  }

  public getProformaInvoiceInfo$(invoiceNumber: string): Observable<HttpResponseBodyNullableType<IFile>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const url: string = `${SERVER_URL}/user/${userId}/proforma-invoice-info?invoiceNo=${invoiceNumber}`;

    return this._httpService.httpClient
      .get<HttpResponseBodyNullableType<IFile>>(url, {
        observe: 'body',
      })
      .pipe(map((response: HttpResponseBodyNullableType<IFile>): HttpResponseBodyNullableType<IFile> => response));
  }

  public getLtLocations$(): Observable<ISelect[]> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .get<IHttpResponseBody<string[]>>(`${SERVER_URL}/user/${userId}/lt/office/lab-location-names`, {
        observe: 'body',
      })
      .pipe(
        map((response: IHttpResponseBody<string[]>): ISelect[] => {
          const data = response.content || [];

          return [{ value: '', label: 'All Locations' }, ...data.map((locationName): ISelect => ({ value: locationName, label: locationName }))];
        })
      );
  }

  public getInvoiceInfo(invoiceNumber: string, rowData?: Record<string, unknown>): void {
    if (rowData?.['proformaInvoiceNumber'] && !rowData?.['invoiceNumber']) {
      this._overlayService.updateIsLoading(true);

      this.getProformaInvoiceInfo$(invoiceNumber)
        .pipe(untilDestroyed(this))
        .subscribe((res): void => {
          if (res?.content) {
            this._appFileDownloadService.downloadDirectly(res.content.fileName, res.content.id);
          }

          this._overlayService.updateIsLoading(false);
        });
    } else if (rowData?.['invoiceNumber']) {
      const userId = this._storageService.getItem(EStorageKeys.USER_ID);
      const url = `${SERVER_URL}/user/${userId}/finance/invoice/${rowData['invoiceNumber'] as string}/url`;

      this._httpService.httpClient
        .get(url)
        .pipe(untilDestroyed(this))
        .subscribe((res: QimaNullableType<IHttpResponseBody<string>>): void => {
          if (res?.message) {
            this._window.open(res.message, '_blank');
          }
        });
    }

    const serviceType = rowData?.['serviceTypeText'] || 'Invoice';
    const orderNo = rowData?.['rfqNumber'];

    // TODO: search the method and move this to  npmSurveyService
    this._npsSurveyService
      .getIsSurveyVisible$()
      .pipe(untilDestroyed(this))
      .subscribe((canOpen: boolean): void => {
        if (!canOpen) {
          return;
        }

        this._dialog.open(NpsSurveyComponent, {
          data: { orderNo, serviceType, npsFrom: ENpsSurveySource.DOWNLOAD_INVOICE },
          stopCloseWhenClickBackdrop: true,
        });
      });
  }

  public getProformaInvoiceInfo(invoiceNumber: string, rowData?: Record<string, unknown>): void {
    if (!invoiceNumber) {
      return;
    }

    this._overlayService.updateIsLoading(true);

    this.getProformaInvoiceInfo$(invoiceNumber)
      .pipe(untilDestroyed(this))
      .subscribe((res): void => {
        if (res?.content) {
          this._appFileDownloadService.downloadDirectly(res.content.fileName, res.content.id);
        }

        this._overlayService.updateIsLoading(false);
      });

    const serviceType = rowData?.['serviceTypeText'] || 'Invoice';
    const orderNo = rowData?.['rfqNumber'];

    // TODO: search the method and move this to  npmSurveyService
    this._npsSurveyService
      .getIsSurveyVisible$()
      .pipe(untilDestroyed(this))
      .subscribe((canOpen: boolean): void => {
        if (!canOpen) {
          return;
        }

        this._dialog.open(NpsSurveyComponent, {
          data: { orderNo, serviceType, npsFrom: ENpsSurveySource.DOWNLOAD_INVOICE },
          stopCloseWhenClickBackdrop: true,
        });
      });
  }

  public getProformaInvoice$(orderId: string): Observable<QimaNullableType<IInvoiceResponse>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .get<IHttpResponseBody<QimaNullableType<IInvoiceResponse>>>(`${SERVER_URL}/user/${userId}/proforma-invoice?orderId=${orderId}`, {
        observe: 'response',
      })
      .pipe(map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<IInvoiceResponse>>>>): QimaNullableType<IInvoiceResponse> => response?.body?.content || null));
  }

  public generateDynamicProformaInvoice$(orderIds: string): Observable<QimaNullableType<IInvoiceResponse>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .post<IHttpResponseBody<QimaNullableType<IInvoiceResponse>>>(
        `${SERVER_URL}/v1.0/order/user/${userId}/proforma-invoice?orderIds=${orderIds}`,
        {},
        {
          observe: 'response',
        }
      )
      .pipe(map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<IInvoiceResponse>>>>): QimaNullableType<IInvoiceResponse> => response?.body?.content || null));
  }

  // non-dynamic
  public generateProformaInvoice$(orderIds: string): Observable<QimaNullableType<IInvoiceResponse>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .post<IHttpResponseBody<QimaNullableType<IInvoiceResponse>>>(
        `${SERVER_URL}/user/${userId}/proforma-invoice?orderIds=${orderIds}`,
        {},
        {
          observe: 'response',
        }
      )
      .pipe(map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<IInvoiceResponse>>>>): QimaNullableType<IInvoiceResponse> => response?.body?.content || null));
  }

  public refreshGenerateProformaInvoice$(piNumbers: string): Observable<QimaNullableType<IInvoiceResponse[]>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .put<IHttpResponseBody<QimaNullableType<IInvoiceResponse[]>>>(
        `${SERVER_URL}/user/${userId}/proforma-invoice-by-pi-number?piNumbers=${encodeURIComponent(piNumbers)}`,
        {},
        {
          observe: 'response',
        }
      )
      .pipe(
        map((response: Readonly<HttpResponse<IHttpResponseBody<QimaNullableType<IInvoiceResponse[]>>>>): QimaNullableType<IInvoiceResponse[]> => response?.body?.content || null)
      );
  }

  public payByTransfer$(orderIds: string[], _orderFees: (IOrderFee | ILabTestingOrderFee)[], uploadFiles: IQimaFile[]): Observable<IHttpResponseBody<undefined>> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);
    const queryString = orderIds.map((id): string => `orderIds=${encodeURIComponent(id)}`).join('&');
    const url = `${SERVER_URL}/order/${userId}/general-info/update-order-waiting-for-payment?${queryString}`;
    const uploadObservables = uploadFiles.map(
      (file): Observable<IEvidenceUploadStatus> =>
        this._appFileService.uploadFiles$(orderIds.join(','), EDocType.WAITING_FOR_PAYMENT, file).pipe(
          map((res: QimaNullableType<IFileResponse>): IEvidenceUploadStatus => ({ source: file, result: res, code: 'success' })),
          catchError((error): Observable<IEvidenceUploadStatus> => of({ source: file, result: error, code: 'error' }))
        )
    );

    return forkJoin(uploadObservables).pipe(
      switchMap((): Observable<IHttpResponseBody<undefined>> => this._httpService.httpClient.post<IHttpResponseBody<undefined>>(url, null, { observe: 'body' }))
    );
  }

  public getOrderFees$(
    orderIds: string[],
    _orderFees: (IOrderFee | ILabTestingOrderFee)[],
    orderIdToRefMap: Record<string, PageItemType>
  ): Observable<QimaOptionalType<IOrderFee[] | ILabTestingInvoices>> {
    return this._orderFeeService.getOrderFees$(orderIds).pipe(
      tap({
        next(orderFeesResult: QimaOptionalType<IOrderFee[]>): void {
          if (!orderFeesResult) {
            return;
          }

          _orderFees = orderFeesResult.map((item: IOrderFee): IOrderFee => {
            item.adyenRefNumber = item.orderId ? (orderIdToRefMap[item.orderId] as string) : null;

            return item;
          });
        },
      })
    );
  }

  /*
   * 1. Get dynamic booking menus if they were enabled for current client
   * 2. hide the menu that are not in the payment categories or its count is 0
   */
  // eslint-disable-next-line rxjs/finnish
  public getBusinessSolutions(shouldHideEmptyTable = false): Observable<Array<ISelect>> {
    const paymentsCategories$: Observable<IPaymentCategories> = shouldHideEmptyTable ? this.getUnpaidPaymentsCategories$() : of({});

    return forkJoin([this._asideHttpService.getDynamicBookingMenus$(), paymentsCategories$]).pipe(
      map(([menus, paymentsCategories]): Array<ISelect> => {
        // 1. set the default business solutions for payments
        let businessSolutions = [
          ...DEFAULT_BUSINESS_SOLUTIONS,
          {
            label: 'DEBIT_NOTES.TITLE',
            value: EBusinessSolution.DEBIT_NOTE,
          },
        ];
        // 2. attach menus for dynamic form service types
        const keys: string[] = Object.keys(menus as Record<string, unknown>);
        const isEmbedInAca = this._router.url.startsWith('/payments-for-aca');

        businessSolutions.push(
          ...keys.map(
            (key: string): ISelect => ({
              label: getDynamicBusinessSolutionLabel(key, menus[key][0].serviceCategory, isEmbedInAca),
              value: menus[key][0].serviceCategory.toLowerCase(),
              type: menus[key][0].serviceType,
            })
          )
        );

        // 3. hide empty business solutions
        if (shouldHideEmptyTable) {
          businessSolutions = businessSolutions.filter((item: ISelect): boolean => !!paymentsCategories[item.value as keyof IPaymentCategories]);
        }

        void this._userIsolatedLocalForageService.setItem(ELocalForageKeys.DYNAMIC_BOOKING_MENUS, menus);

        return businessSolutions;
      })
    );
  }

  public getUnpaidPaymentsCategories$(): Observable<IPaymentCategories> {
    const userId = this._storageService.getItem(EStorageKeys.USER_ID);

    return this._httpService.httpClient
      .get<IHttpResponseBody<IDynamicBookingMenus>>(`${SERVER_URL}/user/${userId}/payment-categories`, {
        observe: 'body',
      })
      .pipe(map((response: IHttpResponseBody<IPaymentCategories>): IPaymentCategories => (response as IPaymentCategories) || {}));
  }

  private _openPopUpWindow(url: string | null, w: number | null, h: number | null): Window | null {
    const window = this._window;

    w = w || 800;
    h = h || 700;
    url = url || '';
    // Fixes dual-screen position Most browsers Firefox
    const dualScreenLeft = window.screenLeft;
    const dualScreenTop = window.screenTop;
    let { width } = screen;

    if (window.innerWidth) {
      width = window.innerWidth;
    } else if (document.documentElement.clientWidth) {
      width = document.documentElement.clientWidth;
    }

    let { height } = screen;

    if (window.innerHeight) {
      height = window.innerHeight;
    } else if (document.documentElement.clientHeight) {
      height = document.documentElement.clientHeight;
    }

    const left = width / 2 - w / 2 + dualScreenLeft;
    const top = height / 2 - h / 2 + dualScreenTop;

    return window.open(url, 'AIPayment', `scrollbars=yes, width=${w}, height=${h}, top=${top}, left=${left}`);
  }
}
